import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Pencil, Trash2, X, Check, Shield, Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { useSmartRefresh, WS_EVENTS } from '../hooks/useSmartRefresh';

const ROLE_LABELS = {
  admin: 'Administrateur',
  rh: 'Ressources Humaines',
  responsable: 'Responsable',
  chauffeur: 'Chauffeur'
};

const PERMISSION_LABELS = {
  dashboard: 'Tableau de bord',
  employees: 'Salariés',
  vehicles: 'Flotte',
  map: 'Géolocalisation',
  planning: 'Planning',
  tasks: 'Tâches',
  incidents: 'Incidents',
  warnings: 'Disciplinaire',
  admin: 'Administration',
  settings: 'Paramètres'
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showStoredPassword, setShowStoredPassword] = useState(false);
  const [storedPassword, setStoredPassword] = useState('');
  const { theme } = useTheme();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    role: 'chauffeur',
    permissions: [],
    actif: true
  });

  useEffect(() => {
    fetchUsers();
    fetchDefaultPermissions();
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Smart refresh: WebSocket + polling fallback 30s
  useSmartRefresh(fetchUsers, {
    wsEvents: [WS_EVENTS.USER_UPDATED, WS_EVENTS.USER_CREATED, WS_EVENTS.USER_DELETED],
    fallbackInterval: 30000
  });

  const fetchDefaultPermissions = async () => {
    try {
      const response = await api.get('/admin/available-permissions');
      setDefaultPermissions(response.data.default_by_role || {});
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleRoleChange = (role) => {
    const rolePermissions = defaultPermissions[role] || [];
    setFormData(prev => ({
      ...prev,
      role,
      permissions: rolePermissions
    }));
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const openCreateModal = () => {
    setEditingUser(null);
    const defaultRole = 'chauffeur';
    setFormData({
      email: '',
      password: '',
      nom: '',
      prenom: '',
      role: defaultRole,
      permissions: defaultPermissions[defaultRole] || ['dashboard', 'planning', 'tasks', 'incidents', 'settings'],
      actif: true
    });
    setShowModal(true);
  };

  const openEditModal = async (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      nom: user.nom,
      prenom: user.prenom,
      role: user.role,
      permissions: user.permissions || [],
      actif: user.actif !== false
    });
    setShowStoredPassword(false);
    setStoredPassword('');
    // Fetch user details to get password_plain
    try {
      const res = await api.get(`/admin/users/${user.id}`);
      setStoredPassword(res.data.password_plain || '');
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (editingUser && !payload.password) {
        delete payload.password;
      }

      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, payload);
        toast.success('Utilisateur modifié avec succès');
      } else {
        await api.post('/admin/users', payload);
        toast.success('Utilisateur créé avec succès');
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`Supprimer l'utilisateur ${user.prenom} ${user.nom} ?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'rh':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'responsable':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-0 pb-24 md:pb-0" data-testid="admin-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Users size={24} className="text-red-400 flex-shrink-0 md:w-7 md:h-7" />
          <h1 className={cn("text-xl md:text-2xl font-bold truncate", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
            <span className="hidden md:inline">Gestion des Utilisateurs</span>
            <span className="md:hidden">Utilisateurs</span>
          </h1>
        </div>
        <Button onClick={openCreateModal} variant="brand" data-testid="create-user-button" className="h-10 w-10 p-0 md:h-auto md:w-auto md:px-4 md:py-2 flex-shrink-0">
          <Plus size={18} className="md:mr-2" />
          <span className="hidden md:inline">Nouvel Utilisateur</span>
        </Button>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {users.map((user) => (
          <div 
            key={user.id} 
            className={cn(
              "rounded-xl border p-4 space-y-3",
              theme === 'dark' ? 'bg-black/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
            )}
            data-testid={`user-card-${user.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className={cn("font-semibold text-base", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  {user.prenom} {user.nom}
                </div>
                <div className={cn("text-sm break-all mt-1", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                  {user.email}
                </div>
              </div>
              <div className="flex gap-2 ml-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(user)}
                  className={cn(
                    "h-8 w-8 p-0",
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                  data-testid={`edit-user-mobile-${user.id}`}
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(user)}
                  className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
                  data-testid={`delete-user-mobile-${user.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadgeClass(user.role)}`}>
                {ROLE_LABELS[user.role] || user.role}
              </span>
              {user.actif !== false ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
                  <Check size={12} /> Actif
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                  <X size={12} /> Inactif
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className={cn(
        "hidden md:block overflow-hidden rounded-xl border",
        theme === 'dark' ? 'bg-black/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
      )}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={cn(
              "border-b",
              theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-gray-50 border-gray-200'
            )}>
              <tr>
                <th className={cn("px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>Utilisateur</th>
                <th className={cn("px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>Email</th>
                <th className={cn("px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>Rôle</th>
                <th className={cn("px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>Accès</th>
                <th className={cn("px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>Statut</th>
                <th className={cn("px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>Actions</th>
              </tr>
            </thead>
            <tbody className={cn("divide-y", theme === 'dark' ? 'divide-white/5' : 'divide-gray-100')}>
              {users.map((user) => (
                <tr key={user.id} className={cn("transition-colors", theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50')} data-testid={`user-row-${user.id}`}>
                  <td className="px-6 py-4">
                    <div className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>{user.prenom} {user.nom}</div>
                  </td>
                  <td className={cn("px-6 py-4", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadgeClass(user.role)}`}>
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(user.permissions || []).slice(0, 4).map(perm => (
                        <span key={perm} className={cn(
                          "inline-flex px-1.5 py-0.5 text-xs rounded",
                          theme === 'dark' ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-600'
                        )}>
                          {PERMISSION_LABELS[perm] || perm}
                        </span>
                      ))}
                      {(user.permissions || []).length > 4 && (
                        <span className={cn(
                          "inline-flex px-1.5 py-0.5 text-xs rounded",
                          theme === 'dark' ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-600'
                        )}>
                          +{user.permissions.length - 4}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.actif !== false ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
                        <Check size={12} /> Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                        <X size={12} /> Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(user)}
                        className={cn(
                          theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                        )}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(user)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className={cn("text-center py-12", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
            Aucun utilisateur trouvé
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={cn(
            "rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border",
            theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
          )}>
            <div className={cn(
              "flex items-center justify-between p-6 border-b",
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>
              <h2 className={cn("text-xl font-semibold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel Utilisateur'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className={cn(
                  "p-2 rounded-lg transition-colors",
                  theme === 'dark' ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-gray-100 text-gray-500'
                )}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prenom" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData(prev => ({ ...prev, prenom: e.target.value }))}
                    required
                    className={cn(
                      "mt-1",
                      theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    )}
                    data-testid="input-prenom"
                  />
                </div>
                <div>
                  <Label htmlFor="nom" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                    required
                    className={cn(
                      "mt-1",
                      theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    )}
                    data-testid="input-nom"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className={cn(
                    "mt-1",
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                  data-testid="input-email"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  Mot de passe {editingUser ? '(laisser vide pour ne pas modifier)' : '*'}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required={!editingUser}
                    className={cn(
                      "pr-10",
                      theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    )}
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={cn(
                      "absolute right-3 top-1/2 -translate-y-1/2",
                      theme === 'dark' ? 'text-zinc-500 hover:text-zinc-300' : 'text-gray-400 hover:text-gray-600'
                    )}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Stored Password (admin only, edit mode) */}
              {editingUser && storedPassword && (
                <div className={cn(
                  "p-3 rounded-lg border",
                  theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-gray-50 border-gray-200'
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock size={14} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
                      <span className={cn("text-xs font-medium uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                        Mot de passe actuel
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowStoredPassword(!showStoredPassword)}
                      className={cn(
                        "flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors",
                        theme === 'dark' ? 'text-zinc-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                      )}
                      data-testid="toggle-stored-password"
                    >
                      {showStoredPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      {showStoredPassword ? 'Masquer' : 'Afficher'}
                    </button>
                  </div>
                  {showStoredPassword && (
                    <div className={cn(
                      "mt-2 px-3 py-2 rounded font-mono text-sm select-all",
                      theme === 'dark' ? 'bg-black text-white border border-white/10' : 'bg-white text-gray-900 border border-gray-200'
                    )} data-testid="stored-password-value">
                      {storedPassword}
                    </div>
                  )}
                </div>
              )}
              
              {/* Role Selection */}
              <div>
                <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Rôle *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleRoleChange(value)}
                      className={cn(
                        "p-3 rounded-lg border-2 transition-colors text-sm font-medium",
                        formData.role === value
                          ? 'border-red-500 bg-red-500/20 text-red-400'
                          : theme === 'dark' 
                            ? 'border-white/10 hover:border-white/20 text-zinc-400 hover:text-white'
                            : 'border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700'
                      )}
                      data-testid={`role-${value}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Permissions */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={18} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
                  <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Accès aux modules</Label>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(PERMISSION_LABELS).map(([value, label]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        formData.permissions.includes(value)
                          ? 'border-cyan-500 bg-cyan-500/20'
                          : theme === 'dark' 
                            ? 'border-white/10 hover:border-white/20'
                            : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(value)}
                        onChange={() => handlePermissionToggle(value)}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-cyan-500 focus:ring-cyan-500"
                        data-testid={`perm-${value}`}
                      />
                      <span className={cn(
                        "text-sm",
                        formData.permissions.includes(value) 
                          ? 'text-cyan-400 font-medium' 
                          : theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
                      )}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Status */}
              {editingUser && (
                <div>
                  <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Statut du compte</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.actif}
                        onChange={() => setFormData(prev => ({ ...prev, actif: true }))}
                        className="w-4 h-4 text-green-500 bg-zinc-700 border-zinc-600 focus:ring-green-500"
                      />
                      <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>Actif</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!formData.actif}
                        onChange={() => setFormData(prev => ({ ...prev, actif: false }))}
                        className="w-4 h-4 text-red-500 bg-zinc-700 border-zinc-600 focus:ring-red-500"
                      />
                      <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>Inactif</span>
                    </label>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className={cn("flex justify-end gap-3 pt-4 border-t", theme === 'dark' ? 'border-white/10' : 'border-gray-200')}>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className={cn(
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="brand" data-testid="submit-user-form">
                  {editingUser ? 'Enregistrer' : 'Créer l\'utilisateur'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
